/**
 * ShotAnalyzerReport — printable PDF template for one shot analysis.
 *
 * Rendered off-screen (fixed at `left: -9999px`) so it never appears
 * on the live page. We capture this DOM with `html2canvas`, hand the
 * image to `jsPDF`, and save the result. All styles must be inline so
 * the capture doesn't depend on the rest of the app's CSS bundle.
 *
 * Charts are inline SVG (no chart library) to keep the bundle small
 * and the rendering deterministic across browsers.
 */
import React, { forwardRef } from 'react';

// Same palette as the live UI so the PDF feels like part of the brand.
const NAVY = '#0A192F';
const NAVY_DEEP = '#061224';
const GOLD = '#D4AF37';
const GOLD_SOFT = 'rgba(212,175,55,0.18)';
const TEXT = '#E2E8F0';
const TEXT_DIM = '#94A3B8';
const RED = '#F87171';
const GREEN = '#34D399';

const METRIC_DEFS = [
  { key: 'elbow_angle_release', labelKey: 'sa_m_elbow',     unit: '°',    target: 90, range: [60, 120], invertDistance: true },
  { key: 'knee_bend_min',       labelKey: 'sa_m_knee',      unit: '°',    target: 130, range: [90, 180], invertDistance: true },
  { key: 'balance_score',       labelKey: 'sa_m_balance',   unit: '/100', target: 100, range: [0, 100] },
  { key: 'shoulder_alignment',  labelKey: 'sa_m_align',     unit: '/100', target: 100, range: [0, 100] },
  { key: 'rhythm_score',        labelKey: 'sa_m_rhythm',    unit: '/100', target: 100, range: [0, 100] },
  { key: 'leg_drive_score',     labelKey: 'sa_m_leg_drive', unit: '/100', target: 100, range: [0, 100] },
];

// Phase 2 — ball + arc metric definitions, rendered as their own section.
const BALL_METRIC_DEFS = [
  { key: 'release_angle_deg', labelKey: 'sa_m_release_angle', unit: '°',    target: 48,  range: [30, 70], invertDistance: true },
  { key: 'arc_quality_score', labelKey: 'sa_m_arc_quality',   unit: '/100', target: 100, range: [0, 100] },
  { key: 'apex_height_norm',  labelKey: 'sa_m_apex',          unit: '',     target: 0.85, range: [0.4, 1.0], invertDistance: true },
  { key: 'flight_time_ms',    labelKey: 'sa_m_flight_time',   unit: ' ms',  target: 600, range: [200, 1000], invertDistance: true },
];

// Translate a raw metric to a 0..100 "quality" score for the bar chart.
// For angle metrics with a target, score = how close we are to the target
// within the acceptable range. For 0..100 scores we use them as-is.
function metricQuality(def, value) {
  if (value == null) return 0;
  if (!def.invertDistance) return Math.max(0, Math.min(100, value));
  const distance = Math.abs(value - def.target);
  const tolerance = (def.range[1] - def.range[0]) / 2;
  return Math.max(0, Math.min(100, Math.round(100 * (1 - distance / tolerance))));
}

function FactCell({ label, value, accent }) {
  return (
    <div style={{ background: NAVY_DEEP, borderRadius: 8, padding: 12 }}>
      <div style={{ fontSize: 9, color: TEXT_DIM, letterSpacing: 2, textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 24, color: accent || TEXT, fontWeight: 700, marginTop: 4, fontFamily: 'Arial, sans-serif' }}>{value}</div>
    </div>
  );
}

/**
 * TrajectoryMini — small inline SVG showing the ball arc in the PDF.
 * Coordinates are normalized [0..1] in image space (y grows down).
 */
function TrajectoryMini({ trajectory, rim }) {
  if (!trajectory || trajectory.length < 2) {
    return (
      <div style={{
        height: 130,
        background: NAVY,
        border: `1px dashed ${TEXT_DIM}`,
        borderRadius: 6,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: TEXT_DIM,
        fontSize: 10,
        fontStyle: 'italic',
      }}>—</div>
    );
  }
  const W = 220, H = 130;
  // Auto-fit the trajectory so even off-camera shots stay visible.
  let minX = 1, maxX = 0, minY = 1, maxY = 0;
  for (const p of trajectory) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
  }
  if (rim) {
    minX = Math.min(minX, rim.x); maxX = Math.max(maxX, rim.x);
    minY = Math.min(minY, rim.y); maxY = Math.max(maxY, rim.y);
  }
  const pad = 0.08;
  const spanX = Math.max(0.05, maxX - minX);
  const spanY = Math.max(0.05, maxY - minY);
  const fx = (x) => 6 + ((x - minX + pad * spanX) / (spanX * (1 + 2 * pad))) * (W - 12);
  const fy = (y) => 6 + ((y - minY + pad * spanY) / (spanY * (1 + 2 * pad))) * (H - 12);
  const points = trajectory.map((p) => `${fx(p.x).toFixed(1)},${fy(p.y).toFixed(1)}`).join(' ');
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ background: NAVY, borderRadius: 6 }}>
      <rect x="0" y="0" width={W} height={H} fill={NAVY} stroke={NAVY_DEEP} />
      <polyline points={points} fill="none" stroke={GOLD} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {/* Release point */}
      <circle cx={fx(trajectory[0].x)} cy={fy(trajectory[0].y)} r="3.5" fill={GREEN} />
      {/* Apex (min y) */}
      {(() => {
        const apex = trajectory.reduce((a, b) => (b.y < a.y ? b : a), trajectory[0]);
        return <circle cx={fx(apex.x)} cy={fy(apex.y)} r="3.5" fill="#FBBF24" />;
      })()}
      {rim && (
        <g>
          <circle cx={fx(rim.x)} cy={fy(rim.y)} r="6" fill="none" stroke="#FB923C" strokeWidth="1.5" />
          <text x={fx(rim.x)} y={fy(rim.y) - 9} fontSize="8" fill="#FB923C" textAnchor="middle" fontFamily="Arial">RIM</text>
        </g>
      )}
    </svg>
  );
}

function ScoreGauge({ score }) {
  // Half-circle gauge: 180° sweep from -180 (left) to 0 (right).
  const R = 90;
  const C = Math.PI * R; // half-circle circumference
  const pct = Math.max(0, Math.min(100, score)) / 100;
  const dash = pct * C;
  // Color shift: red < 40, amber 40-70, green > 70.
  const color = score < 40 ? RED : score < 70 ? '#FBBF24' : GREEN;
  return (
    <svg width="220" height="130" viewBox="0 0 220 130">
      <path d={`M 20 110 A ${R} ${R} 0 0 1 200 110`} fill="none" stroke={NAVY_DEEP} strokeWidth="18" strokeLinecap="round" />
      <path
        d={`M 20 110 A ${R} ${R} 0 0 1 200 110`}
        fill="none"
        stroke={color}
        strokeWidth="18"
        strokeLinecap="round"
        strokeDasharray={`${dash} ${C}`}
      />
      <text x="110" y="95" textAnchor="middle" fontSize="44" fontWeight="700" fill={TEXT} fontFamily="Arial, sans-serif">{Math.round(score)}</text>
      <text x="110" y="118" textAnchor="middle" fontSize="11" fill={TEXT_DIM} fontFamily="Arial, sans-serif" letterSpacing="2">/100</text>
    </svg>
  );
}

function MetricBar({ label, raw, unit, quality }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 4 }}>
        <span style={{ color: TEXT, fontWeight: 600 }}>{label}</span>
        <span style={{ color: TEXT_DIM, fontFamily: 'monospace' }}>
          {raw != null ? raw : '—'}{unit}
        </span>
      </div>
      <div style={{ width: '100%', height: 8, background: NAVY_DEEP, borderRadius: 4, overflow: 'hidden' }}>
        <div
          style={{
            width: `${quality}%`,
            height: '100%',
            background: quality >= 70 ? GREEN : quality >= 40 ? '#FBBF24' : RED,
            transition: 'none',
          }}
        />
      </div>
    </div>
  );
}

function ProgressChart({ history }) {
  // history is most-recent-first. Reverse so x-axis flows left-to-right.
  const series = [...history].reverse().slice(-10);
  if (series.length < 2) {
    return (
      <div style={{ height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', color: TEXT_DIM, fontSize: 11, fontStyle: 'italic' }}>
        — sin datos suficientes —
      </div>
    );
  }
  const W = 480, H = 120, PAD = 18;
  const xs = series.map((_, i) => PAD + (i * (W - PAD * 2)) / (series.length - 1));
  const ys = series.map((a) => H - PAD - ((a.overall_score || 0) / 100) * (H - PAD * 2));
  const points = xs.map((x, i) => `${x},${ys[i]}`).join(' ');
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet">
      {/* gridlines at 25/50/75 */}
      {[25, 50, 75].map((g) => {
        const y = H - PAD - (g / 100) * (H - PAD * 2);
        return <line key={g} x1={PAD} x2={W - PAD} y1={y} y2={y} stroke={NAVY_DEEP} strokeDasharray="2 3" />;
      })}
      {/* line */}
      <polyline points={points} fill="none" stroke={GOLD} strokeWidth="2.5" />
      {/* dots */}
      {xs.map((x, i) => (
        <circle key={i} cx={x} cy={ys[i]} r="3.5" fill={GOLD} />
      ))}
      {/* baseline */}
      <line x1={PAD} x2={W - PAD} y1={H - PAD} y2={H - PAD} stroke={TEXT_DIM} strokeWidth="0.8" />
    </svg>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <div
        style={{
          fontSize: 10,
          color: GOLD,
          letterSpacing: 4,
          textTransform: 'uppercase',
          fontWeight: 700,
          marginBottom: 8,
          fontFamily: 'Arial, sans-serif',
        }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

const ShotAnalyzerReport = forwardRef(function ShotAnalyzerReport(
  { attempt, history, aiReport, userName, logoUrl, t, lang = 'es' },
  ref,
) {
  if (!attempt) return null;
  const metrics = attempt.metrics || {};
  const errors = attempt.errors || [];
  const dateStr = new Date(attempt.created_at || Date.now()).toLocaleString(lang === 'es' ? 'es-AR' : lang === 'fr' ? 'fr-FR' : 'en-US', {
    year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });

  // Build derived data for progress section.
  const previousScores = history?.filter((h) => h.id !== attempt.id) || [];
  const lastScore = previousScores.length ? previousScores[0]?.overall_score : null;
  const delta = lastScore != null ? attempt.overall_score - lastScore : null;
  const allScores = history?.map((h) => h.overall_score).filter((x) => x != null) || [];
  const avgScore = allScores.length ? Math.round(allScores.reduce((a, b) => a + b, 0) / allScores.length) : null;
  const maxScore = allScores.length ? Math.max(...allScores) : null;

  return (
    <div
      ref={ref}
      data-testid="sa-report-template"
      style={{
        position: 'fixed',
        left: '-9999px',
        top: 0,
        width: 794,             // A4 width @ 96dpi
        background: NAVY,
        color: TEXT,
        padding: 32,
        fontFamily: 'Arial, Helvetica, sans-serif',
        boxSizing: 'border-box',
      }}
    >
      {/* ── Header ───────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `2px solid ${GOLD}`, paddingBottom: 14, marginBottom: 22 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {logoUrl && (
            <img
              src={logoUrl}
              alt="logo"
              style={{ width: 54, height: 54, objectFit: 'contain' }}
              crossOrigin="anonymous"
            />
          )}
          <div>
            <div style={{ fontSize: 10, color: GOLD, letterSpacing: 4, textTransform: 'uppercase', fontWeight: 700 }}>
              PlayMaster IA · Shot Analyzer
            </div>
            <div style={{ fontSize: 22, color: TEXT, fontWeight: 700, marginTop: 4 }}>
              {t('sa_pdf_title')}
            </div>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 10, color: TEXT_DIM, letterSpacing: 2, textTransform: 'uppercase' }}>{t('sa_pdf_player')}</div>
          <div style={{ fontSize: 14, color: TEXT, fontWeight: 600 }}>{userName || '—'}</div>
          <div style={{ fontSize: 10, color: TEXT_DIM, marginTop: 6 }}>{dateStr}</div>
        </div>
      </div>

      {/* ── Overall + key facts ──────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 18, marginBottom: 22 }}>
        <div style={{ background: GOLD_SOFT, border: `1px solid ${GOLD}`, borderRadius: 10, padding: 16, flex: '0 0 240px', textAlign: 'center' }}>
          <div style={{ fontSize: 10, color: GOLD, letterSpacing: 4, textTransform: 'uppercase', fontWeight: 700, marginBottom: 4 }}>
            {t('sa_overall_score')}
          </div>
          <ScoreGauge score={attempt.overall_score} />
          {delta != null && (
            <div style={{ fontSize: 11, color: delta >= 0 ? GREEN : RED, marginTop: 4, fontWeight: 600 }}>
              {delta >= 0 ? '▲' : '▼'} {Math.abs(delta).toFixed(1)} {t('sa_pdf_vs_last')}
            </div>
          )}
        </div>

        <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <FactCell label={t('sa_pdf_attempts_logged')} value={history?.length || 1} />
          <FactCell label={t('sa_pdf_personal_best')} value={maxScore != null ? maxScore : '—'} />
          <FactCell label={t('sa_pdf_avg')} value={avgScore != null ? avgScore : '—'} />
          <FactCell label={t('sa_pdf_errors_found')} value={errors.length} accent={errors.length === 0 ? GREEN : RED} />
        </div>
      </div>

      {/* ── Metrics ─────────────────────────────────────────────── */}
      <Section title={t('sa_metrics')}>
        <div style={{ background: NAVY_DEEP, padding: 16, borderRadius: 8, border: `1px solid ${NAVY_DEEP}` }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 22 }}>
            {METRIC_DEFS.map((def) => {
              const raw = metrics[def.key];
              const q = metricQuality(def, raw);
              return (
                <MetricBar
                  key={def.key}
                  label={t(def.labelKey)}
                  raw={raw}
                  unit={def.unit}
                  quality={q}
                />
              );
            })}
          </div>
        </div>
      </Section>

      {/* ── Phase 2: Ball trajectory ─────────────────────────────── */}
      {(metrics.release_angle_deg != null || metrics.apex_height_norm != null) && (
        <Section title={t('sa_ball_section')}>
          <div style={{ background: NAVY_DEEP, padding: 16, borderRadius: 8 }}>
            <div style={{ display: 'flex', gap: 18, alignItems: 'flex-start' }}>
              <div style={{ flex: '0 0 230px' }}>
                <TrajectoryMini
                  trajectory={attempt.ball_trajectory}
                  rim={attempt.rim_point}
                />
                {metrics.rim_made != null && (
                  <div style={{
                    marginTop: 8,
                    textAlign: 'center',
                    fontSize: 11,
                    color: metrics.rim_made ? GREEN : RED,
                    fontWeight: 700,
                    letterSpacing: 1,
                    textTransform: 'uppercase',
                  }}>
                    {metrics.rim_made ? `✓ ${t('sa_m_rim_made')}` : `✗ ${t('sa_m_rim_made')}`}
                  </div>
                )}
              </div>
              <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 22 }}>
                {BALL_METRIC_DEFS.map((def) => {
                  const raw = metrics[def.key];
                  if (raw == null) return null;
                  const q = metricQuality(def, raw);
                  return (
                    <MetricBar
                      key={def.key}
                      label={t(def.labelKey)}
                      raw={raw}
                      unit={def.unit}
                      quality={q}
                    />
                  );
                })}
                {metrics.distance_to_rim_norm != null && (
                  <MetricBar
                    label={t('sa_m_rim_distance')}
                    raw={metrics.distance_to_rim_norm}
                    unit=""
                    quality={Math.max(0, Math.min(100, Math.round(100 * (1 - metrics.distance_to_rim_norm))))}
                  />
                )}
              </div>
            </div>
          </div>
        </Section>
      )}

      {/* ── Errors detected ─────────────────────────────────────── */}
      <Section title={t('sa_errors')}>
        {errors.length === 0 ? (
          <div style={{ padding: 14, background: 'rgba(52,211,153,0.08)', border: `1px solid ${GREEN}`, borderRadius: 8, color: GREEN, fontSize: 12 }}>
            {t('sa_pdf_no_errors')}
          </div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {errors.map((e) => (
              <span
                key={e}
                style={{
                  background: 'rgba(248,113,113,0.12)',
                  border: `1px solid ${RED}`,
                  color: RED,
                  padding: '5px 10px',
                  borderRadius: 999,
                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                ⚠ {t(`sa_err_${e}`)}
              </span>
            ))}
          </div>
        )}
      </Section>

      {/* ── AI Coach Report ─────────────────────────────────────── */}
      <Section title={t('sa_ai_report')}>
        <div style={{ background: NAVY_DEEP, border: `1px solid ${GOLD}`, borderRadius: 8, padding: 16, fontSize: 12, lineHeight: 1.7, color: TEXT, whiteSpace: 'pre-wrap' }}>
          {aiReport || t('sa_no_report')}
        </div>
      </Section>

      {/* ── Progress chart ──────────────────────────────────────── */}
      <Section title={t('sa_pdf_progress')}>
        <div style={{ background: NAVY_DEEP, padding: 12, borderRadius: 8 }}>
          <ProgressChart history={history || [attempt]} />
          <div style={{ marginTop: 4, fontSize: 10, color: TEXT_DIM, textAlign: 'center', fontStyle: 'italic' }}>
            {t('sa_pdf_progress_caption')}
          </div>
        </div>
      </Section>

      {/* ── Recommendations ─────────────────────────────────────── */}
      <Section title={t('sa_pdf_recommendations')}>
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, lineHeight: 1.6, color: TEXT }}>
          {buildRecommendations(metrics, errors, t).map((r, i) => (
            <li key={i} style={{ marginBottom: 6 }}>{r}</li>
          ))}
        </ul>
      </Section>

      {/* ── Footer ──────────────────────────────────────────────── */}
      <div style={{ marginTop: 24, borderTop: `1px solid ${NAVY_DEEP}`, paddingTop: 12, fontSize: 9, color: TEXT_DIM, lineHeight: 1.5, fontStyle: 'italic' }}>
        {t('sa_disclaimer')} · playmasteria.com
      </div>
    </div>
  );
});

/**
 * Build a localised list of actionable bullets from the metrics/errors.
 * The Claude report already covers narrative; these are quick scannable
 * checklist items the coach can mark off.
 */
function buildRecommendations(metrics, errors, t) {
  const recs = [];
  if (errors.includes('open_elbow') || (metrics.elbow_angle_release != null && metrics.elbow_angle_release < 80)) {
    recs.push(t('sa_rec_elbow'));
  }
  if (errors.includes('low_release') || (metrics.release_height_norm != null && metrics.release_height_norm < 0.55)) {
    recs.push(t('sa_rec_release'));
  }
  if (errors.includes('no_legs') || (metrics.knee_bend_min != null && metrics.knee_bend_min > 165)) {
    recs.push(t('sa_rec_legs'));
  }
  if (errors.includes('lean_forward') || errors.includes('lean_backward')) {
    recs.push(t('sa_rec_balance'));
  }
  if (errors.includes('broken_rhythm') || (metrics.rhythm_score != null && metrics.rhythm_score < 50)) {
    recs.push(t('sa_rec_rhythm'));
  }
  if (errors.includes('lack_of_fluidity') || (metrics.follow_through_ms != null && metrics.follow_through_ms < 120)) {
    recs.push(t('sa_rec_follow'));
  }
  if (errors.includes('misalignment')) {
    recs.push(t('sa_rec_align'));
  }
  if (errors.includes('flat_arc') || (metrics.release_angle_deg != null && metrics.release_angle_deg < 40)) {
    recs.push(t('sa_rec_flat_arc'));
  }
  if (errors.includes('rainbow_arc') || (metrics.release_angle_deg != null && metrics.release_angle_deg > 58)) {
    recs.push(t('sa_rec_rainbow_arc'));
  }
  if (recs.length === 0) {
    recs.push(t('sa_rec_keep'));
    recs.push(t('sa_rec_volume'));
  } else {
    recs.push(t('sa_rec_record'));
  }
  return recs;
}

export default ShotAnalyzerReport;
